import {
  faChevronLeft,
  faChevronRight,
  faFileInvoiceDollar,
  faRedo,
  faSearch,
  faSort,
  faSyncAlt,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '../../../../../auth0/reactAuth0Spa';
import { checkLoginForError, formatBearerToken } from '../../../../../Common.functions';
import { fetchDataAgnostic } from '../../../../../Services/dataApi';
import style from './FinancialIntelligencePanel.style';

const PAGE_SIZE = 20;
const VIEW_CUSTOMERS = 'customers';
const VIEW_INVOICES = 'invoices';

const localStyle = {
  shell: {
    display: 'grid',
    gap: 10,
    width: '100%',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    padding: 10,
    border: '1px solid #d7dde5',
    borderRadius: 4,
    backgroundColor: '#fbfcfd',
  },
  viewTabs: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  viewButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: '1px solid #c7d2df',
    borderRadius: 4,
    padding: '6px 12px',
    backgroundColor: '#fff',
    color: '#445468',
    fontSize: 12,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  viewButtonActive: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: '1px solid #6f8fb3',
    borderRadius: 4,
    padding: '6px 12px',
    backgroundColor: '#eaf3ff',
    color: '#17324d',
    fontSize: 12,
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.4fr) minmax(320px, 0.8fr)',
    gap: 10,
  },
  pager: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: 10,
    fontSize: 12,
    color: '#445468',
  },
  detailBody: {
    display: 'grid',
    gap: 10,
    padding: 10,
    fontSize: 12,
    color: '#273444',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    gap: 8,
  },
  detailCell: {
    border: '1px solid #d7dde5',
    borderRadius: 4,
    padding: 8,
    backgroundColor: '#fff',
    minWidth: 0,
  },
  cellLabel: {
    color: '#5c6773',
    fontSize: 11,
    marginBottom: 4,
  },
  cellValue: {
    fontWeight: 'bold',
    wordBreak: 'break-word',
  },
  miniSection: {
    display: 'grid',
    gap: 6,
  },
  miniTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#17212b',
  },
};

const customerColumns = [
  { key: 'DepartmentName', label: 'Customer', sortKey: 'name' },
  { key: 'OrganizationName', label: 'Organization', sortKey: 'organization' },
  { key: 'InvoiceCount', label: 'Invoices', sortKey: 'invoiceCount' },
  { key: 'OutstandingBalance', label: 'Outstanding', sortKey: 'outstandingBalance', format: 'money' },
  { key: 'LastInvoiceDate', label: 'Last Invoice', sortKey: 'lastInvoiceDate', format: 'date' },
  { key: 'NeedsReviewCount', label: 'Review' },
  { key: 'NotInvoicedCurrentSeason', label: 'Current Season', format: 'currentSeason' },
];

const invoiceColumns = [
  { key: 'InvoiceNumber', label: 'Invoice', sortKey: 'invoiceNumber' },
  { key: 'QuickBooksCustomerName', label: 'Customer', sortKey: 'customer' },
  { key: 'IssueDate', label: 'Issue', sortKey: 'issueDate', format: 'date' },
  { key: 'DueDate', label: 'Due', sortKey: 'dueDate', format: 'date' },
  { key: 'PaymentStatusLabel', label: 'Payment' },
  { key: 'Status', label: 'Status', sortKey: 'status' },
  { key: 'TotalAmount', label: 'Total', sortKey: 'totalAmount', format: 'money' },
  { key: 'BalanceDue', label: 'Balance', sortKey: 'balanceDue', format: 'money' },
];

const formatMoney = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return '$0.00';
  }
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return `${value}`;
  }
  return date.toLocaleDateString();
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return `${value}`;
};

const formatCellValue = (row, column) => {
  const value = row[column.key];
  if (column.format === 'money') {
    return formatMoney(value);
  }
  if (column.format === 'date') {
    return formatDate(value);
  }
  if (column.format === 'currentSeason') {
    return value ? 'Not invoiced' : 'Covered';
  }
  return formatValue(value);
};

const parseSort = (sort) => {
  if (!sort) {
    return { field: '', direction: 'asc' };
  }
  if (sort.startsWith('-')) {
    return { field: sort.slice(1), direction: 'desc' };
  }
  const [field, direction] = sort.split(':');
  return { field, direction: direction === 'desc' ? 'desc' : 'asc' };
};

const getNextSort = (sort, field) => {
  const parsed = parseSort(sort);
  if (parsed.field !== field) {
    return `${field}:asc`;
  }
  return `${field}:${parsed.direction === 'asc' ? 'desc' : 'asc'}`;
};

const renderDetailMetric = ({ label, value }) => (
  <div key={label} style={localStyle.detailCell}>
    <div style={localStyle.cellLabel}>{label}</div>
    <div style={localStyle.cellValue}>{value}</div>
  </div>
);

const FinanceConsole = () => {
  const { getTokenSilently, loginWithRedirect } = useAuth0();
  const [activeView, setActiveView] = useState(VIEW_CUSTOMERS);
  const [query, setQuery] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState('');
  const [customerSort, setCustomerSort] = useState('name:asc');
  const [invoiceSort, setInvoiceSort] = useState('dueDate:desc');
  const [customerPage, setCustomerPage] = useState(1);
  const [invoicePage, setInvoicePage] = useState(1);
  const [customers, setCustomers] = useState({ rows: [], total: 0, page: 1, pageSize: PAGE_SIZE });
  const [invoices, setInvoices] = useState({ rows: [], total: 0, page: 1, pageSize: PAGE_SIZE });
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [invoiceDetail, setInvoiceDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [message, setMessage] = useState('');

  const activeRows = activeView === VIEW_CUSTOMERS ? customers.rows : invoices.rows;
  const activeTotal = activeView === VIEW_CUSTOMERS ? customers.total : invoices.total;
  const activePage = activeView === VIEW_CUSTOMERS ? customerPage : invoicePage;
  const activeSort = activeView === VIEW_CUSTOMERS ? customerSort : invoiceSort;
  const activeColumns = activeView === VIEW_CUSTOMERS ? customerColumns : invoiceColumns;
  const maxPage = Math.max(1, Math.ceil(activeTotal / PAGE_SIZE));

  const getAuthHeaders = useCallback(
    () => getTokenSilently().then((token) => formatBearerToken(token)),
    [getTokenSilently],
  );

  const loadRows = useCallback(() => {
    setIsLoading(true);
    setMessage('');

    const params = {
      q: query || undefined,
      page: activeView === VIEW_CUSTOMERS ? customerPage : invoicePage,
      pageSize: PAGE_SIZE,
      sort: activeView === VIEW_CUSTOMERS ? customerSort : invoiceSort,
    };

    if (activeView === VIEW_CUSTOMERS && customerFilter) {
      params.filters = customerFilter;
    }
    if (activeView === VIEW_INVOICES && invoiceStatus) {
      params.filters = `status:${invoiceStatus}`;
    }

    const url =
      activeView === VIEW_CUSTOMERS
        ? 'financial-intelligence/console/customers'
        : 'financial-intelligence/console/invoices';

    getAuthHeaders()
      .then((headers) => fetchDataAgnostic(url, params, headers))
      .then((res) => {
        const nextData = {
          rows: (res.data && res.data.rows) || [],
          total: (res.data && Number(res.data.total)) || 0,
          page: (res.data && Number(res.data.page)) || 1,
          pageSize: (res.data && Number(res.data.pageSize)) || PAGE_SIZE,
        };
        if (activeView === VIEW_CUSTOMERS) {
          setCustomers(nextData);
        } else {
          setInvoices(nextData);
        }
      })
      .catch((err) => {
        const apiMessage =
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Finance Console data could not be loaded.';
        setMessage(apiMessage);
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    activeView,
    customerFilter,
    customerPage,
    customerSort,
    getAuthHeaders,
    invoicePage,
    invoiceSort,
    invoiceStatus,
    loginWithRedirect,
    query,
  ]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const loadCustomerDetail = (customer) => {
    setSelectedCustomer(customer);
    setSelectedInvoice(null);
    setInvoiceDetail(null);
    setIsDetailLoading(true);
    setMessage('');

    getAuthHeaders()
      .then((headers) =>
        fetchDataAgnostic(`financial-intelligence/console/customers/${customer.pk_Department}`, {}, headers),
      )
      .then((res) => {
        setCustomerDetail(res.data || null);
      })
      .catch((err) => {
        const apiMessage =
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Customer detail could not be loaded.';
        setMessage(apiMessage);
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setIsDetailLoading(false);
      });
  };

  const loadInvoiceDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setSelectedCustomer(null);
    setCustomerDetail(null);
    setIsDetailLoading(true);
    setMessage('');

    getAuthHeaders()
      .then((headers) =>
        fetchDataAgnostic(`financial-intelligence/console/invoices/${invoice.pk_QuickBooksInvoice}`, {}, headers),
      )
      .then((res) => {
        setInvoiceDetail(res.data || null);
      })
      .catch((err) => {
        const apiMessage =
          err && err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : 'Invoice detail could not be loaded.';
        setMessage(apiMessage);
        checkLoginForError({ err, loginWithRedirect });
      })
      .finally(() => {
        setIsDetailLoading(false);
      });
  };

  const changeSort = (field) => {
    if (activeView === VIEW_CUSTOMERS) {
      setCustomerSort((prevSort) => getNextSort(prevSort, field));
      setCustomerPage(1);
    } else {
      setInvoiceSort((prevSort) => getNextSort(prevSort, field));
      setInvoicePage(1);
    }
  };

  const changePage = (delta) => {
    if (activeView === VIEW_CUSTOMERS) {
      setCustomerPage((page) => Math.min(Math.max(page + delta, 1), maxPage));
    } else {
      setInvoicePage((page) => Math.min(Math.max(page + delta, 1), maxPage));
    }
  };

  const resetPagingForSearch = (value) => {
    setQuery(value);
    setCustomerPage(1);
    setInvoicePage(1);
  };

  const renderTable = () => {
    const parsedSort = parseSort(activeSort);

    return (
      <div style={style.tableWrap}>
        <table style={style.table}>
          <thead>
            <tr>
              {activeColumns.map((column) => (
                <th key={column.key} style={style.th}>
                  {column.sortKey ? (
                    <button
                      type="button"
                      style={{ ...localStyle.viewButton, padding: 0, border: 'none', backgroundColor: 'transparent' }}
                      onClick={() => changeSort(column.sortKey)}
                    >
                      {column.label}
                      <FontAwesomeIcon icon={faSort} />
                      {parsedSort.field === column.sortKey ? parsedSort.direction : null}
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeRows.length > 0 ? (
              activeRows.map((row) => {
                const rowKey =
                  activeView === VIEW_CUSTOMERS ? row.pk_Department : row.pk_QuickBooksInvoice;
                const isSelected =
                  activeView === VIEW_CUSTOMERS
                    ? selectedCustomer && selectedCustomer.pk_Department === row.pk_Department
                    : selectedInvoice && selectedInvoice.pk_QuickBooksInvoice === row.pk_QuickBooksInvoice;

                return (
                  <tr
                    key={rowKey}
                    style={isSelected ? style.selectedTableRow : { cursor: 'pointer' }}
                    onClick={() => {
                      if (activeView === VIEW_CUSTOMERS) {
                        loadCustomerDetail(row);
                      } else {
                        loadInvoiceDetail(row);
                      }
                    }}
                  >
                    {activeColumns.map((column) => (
                      <td key={column.key} style={style.td}>
                        {formatCellValue(row, column)}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={activeColumns.length}>
                  <div style={style.emptyState}>{isLoading ? 'Loading...' : 'No finance console rows found.'}</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCustomerDetail = () => {
    if (!selectedCustomer) {
      return <div style={style.emptyState}>Select a customer to inspect billing history.</div>;
    }

    const data = customerDetail || {};
    const customer = data.customer || selectedCustomer;
    const seasons = data.seasons || [];
    const billingHistory = data.billingHistory || [];

    return (
      <div style={localStyle.detailBody}>
        <div style={localStyle.detailGrid}>
          {renderDetailMetric({ label: 'Customer', value: formatValue(customer.DepartmentName) })}
          {renderDetailMetric({ label: 'Organization', value: formatValue(customer.OrganizationName) })}
          {renderDetailMetric({ label: 'Invoices', value: formatValue(customer.InvoiceCount) })}
          {renderDetailMetric({ label: 'Outstanding', value: formatMoney(customer.OutstandingBalance) })}
        </div>
        <div style={localStyle.miniSection}>
          <div style={localStyle.miniTitle}>Seasons</div>
          {seasons.length ? (
            <div style={style.tableWrap}>
              <table style={style.table}>
                <tbody>
                  {seasons.slice(0, 8).map((season) => (
                    <tr key={season.pk_Season}>
                      <td style={style.td}>{formatValue(season.SeasonName)}</td>
                      <td style={style.td}>{formatValue(season.FinancialStatus)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={style.contextNote}>No season status rows found.</div>
          )}
        </div>
        <div style={localStyle.miniSection}>
          <div style={localStyle.miniTitle}>Billing History</div>
          {billingHistory.length ? (
            <div style={style.tableWrap}>
              <table style={style.table}>
                <tbody>
                  {billingHistory.slice(0, 8).map((invoice) => (
                    <tr key={invoice.pk_QuickBooksInvoice}>
                      <td style={style.td}>{formatValue(invoice.InvoiceNumber)}</td>
                      <td style={style.td}>{formatDate(invoice.IssueDate)}</td>
                      <td style={style.td}>{formatMoney(invoice.BalanceDue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={style.contextNote}>No billing history found.</div>
          )}
        </div>
      </div>
    );
  };

  const renderInvoiceDetail = () => {
    if (!selectedInvoice) {
      return <div style={style.emptyState}>Select an invoice to inspect line items and payments.</div>;
    }

    const invoice = invoiceDetail || selectedInvoice;
    const lineItems = invoice.lineItems || [];
    const payments = invoice.payments || [];

    return (
      <div style={localStyle.detailBody}>
        <div style={localStyle.detailGrid}>
          {renderDetailMetric({ label: 'Invoice', value: formatValue(invoice.InvoiceNumber) })}
          {renderDetailMetric({ label: 'Customer', value: formatValue(invoice.QuickBooksCustomerName) })}
          {renderDetailMetric({ label: 'Total', value: formatMoney(invoice.TotalAmount) })}
          {renderDetailMetric({ label: 'Balance', value: formatMoney(invoice.BalanceDue) })}
          {renderDetailMetric({ label: 'Payment', value: formatValue(invoice.PaymentStatusLabel) })}
          {renderDetailMetric({ label: 'Close-out', value: formatValue(invoice.CloseOutStatusLabel) })}
        </div>
        <div style={localStyle.miniSection}>
          <div style={localStyle.miniTitle}>Line Items</div>
          {lineItems.length ? (
            <div style={style.tableWrap}>
              <table style={style.table}>
                <tbody>
                  {lineItems.slice(0, 8).map((lineItem) => (
                    <tr key={lineItem.pk_QuickBooksInvoiceLineItem}>
                      <td style={style.td}>{formatValue(lineItem.Description || lineItem.ProductSku)}</td>
                      <td style={style.td}>{formatValue(lineItem.Quantity)}</td>
                      <td style={style.td}>{formatMoney(lineItem.LineAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={style.contextNote}>No line items found.</div>
          )}
        </div>
        <div style={localStyle.miniSection}>
          <div style={localStyle.miniTitle}>Payments</div>
          {payments.length ? (
            <div style={style.tableWrap}>
              <table style={style.table}>
                <tbody>
                  {payments.slice(0, 8).map((payment) => (
                    <tr key={payment.pk_QuickBooksPayment}>
                      <td style={style.td}>{formatDate(payment.PaymentDate)}</td>
                      <td style={style.td}>{formatMoney(payment.Amount)}</td>
                      <td style={style.td}>{formatValue(payment.PaymentMethod)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={style.contextNote}>No payments found.</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={localStyle.shell}>
      <div style={localStyle.topBar}>
        <div>
          <div style={style.billingTitle}>Finance Console</div>
          <div style={style.contextNote}>Super-user customer and invoice master tables from QuickBooks billing data.</div>
        </div>
        <div style={localStyle.viewTabs}>
          <button
            type="button"
            style={activeView === VIEW_CUSTOMERS ? localStyle.viewButtonActive : localStyle.viewButton}
            onClick={() => setActiveView(VIEW_CUSTOMERS)}
          >
            <FontAwesomeIcon icon={faUsers} />
            Customers
          </button>
          <button
            type="button"
            style={activeView === VIEW_INVOICES ? localStyle.viewButtonActive : localStyle.viewButton}
            onClick={() => setActiveView(VIEW_INVOICES)}
          >
            <FontAwesomeIcon icon={faFileInvoiceDollar} />
            Invoices
          </button>
        </div>
      </div>

      <div style={style.relationPanel}>
        <div style={style.toolbar}>
          <FontAwesomeIcon icon={faSearch} />
          <input
            aria-label="Search finance console"
            style={style.toolbarInput}
            value={query}
            placeholder={activeView === VIEW_CUSTOMERS ? 'Search customers or organizations' : 'Search invoices or customers'}
            onChange={(e) => resetPagingForSearch(e.target.value)}
          />
          {activeView === VIEW_CUSTOMERS ? (
            <select
              aria-label="Customer finance filter"
              style={style.toolbarSelect}
              value={customerFilter}
              onChange={(e) => {
                setCustomerFilter(e.target.value);
                setCustomerPage(1);
              }}
            >
              <option value="">All customers</option>
              <option value="outstandingInvoices">Outstanding invoices</option>
              <option value="fullyPaid">Fully paid</option>
              <option value="notInvoicedCurrentSeason">Not invoiced current season</option>
              <option value="needsReview">Needs review</option>
            </select>
          ) : (
            <select
              aria-label="Invoice status filter"
              style={style.toolbarSelect}
              value={invoiceStatus}
              onChange={(e) => {
                setInvoiceStatus(e.target.value);
                setInvoicePage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="Paid">Paid</option>
              <option value="Open">Open</option>
              <option value="Overdue">Overdue</option>
              <option value="Draft">Draft</option>
              <option value="Voided">Voided</option>
            </select>
          )}
          <button type="button" style={style.actionButton} onClick={loadRows} disabled={isLoading}>
            <FontAwesomeIcon icon={isLoading ? faSyncAlt : faRedo} spin={isLoading} />
            Refresh
          </button>
          {message ? <div style={style.actionMessage}>{message}</div> : null}
        </div>

        <div style={localStyle.contentGrid}>
          <div>
            {renderTable()}
            <div style={localStyle.pager}>
              <button type="button" style={style.iconActionButton} disabled={activePage <= 1} onClick={() => changePage(-1)}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <span>
                Page {activePage} of {maxPage} · {activeTotal} rows
              </span>
              <button
                type="button"
                style={style.iconActionButton}
                disabled={activePage >= maxPage}
                onClick={() => changePage(1)}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>

          <div style={style.relationPanel}>
            <div style={style.relationHeader}>
              <div>
                <div style={style.relationTitle}>
                  {activeView === VIEW_CUSTOMERS ? 'Customer Detail' : 'Invoice Detail'}
                </div>
                <div style={style.contextNote}>{isDetailLoading ? 'Loading detail...' : 'Drill-down from the selected row.'}</div>
              </div>
            </div>
            {activeView === VIEW_CUSTOMERS ? renderCustomerDetail() : renderInvoiceDetail()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinanceConsole;
